import { useTts } from '../state/TtsContext';
import { useT } from '../hooks/useT';

interface SpeakButtonProps {
  /** The exact text to read out loud. */
  text: string;
  /** Optional English text, used when the display mode is English. */
  textEn?: string;
  /** Stable id so this button can highlight while it is the one speaking. */
  id: string;
  size?: 'md' | 'lg';
  /** Accessible name. Defaults to "Listen to this answer". */
  label?: string;
}

/**
 * The 🔊 button. Every question, every answer and every explanation has one.
 * It is disabled (with an explanation) when no voice exists for the language.
 */
export function SpeakButton({ text, textEn, id, size = 'md', label }: SpeakButtonProps) {
  const t = useT();
  const { speak, stop, status, canSpeakText, speechLang } = useTts();

  const isActive = status.currentItemId === id && status.state !== 'idle';
  const spoken = speechLang === 'en' ? (textEn ?? text) : text;
  // Asked per phrase, not per language: with no device voice only the recorded
  // phrases can be spoken, and a button that does nothing is worse than one
  // that plainly says it cannot.
  const disabled = spoken.trim().length === 0 || !canSpeakText(spoken, speechLang);

  return (
    <button
      type="button"
      className={[
        'speak-btn',
        size === 'lg' ? 'speak-btn--lg' : '',
        isActive ? 'speak-btn--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={label ?? t('listenAnswer')}
      aria-pressed={isActive}
      disabled={disabled}
      title={disabled ? t('noArabicVoiceTitle') : (label ?? t('listenAnswer'))}
      onClick={() => {
        if (isActive) stop();
        else speak(spoken, speechLang, id);
      }}
    >
      <span aria-hidden="true">{isActive ? '⏹' : '🔊'}</span>
    </button>
  );
}
